import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { createHotel } from "@/lib/hotelsApi";

interface JsonRoom {
  id?: number | string;
  hotel_id?: number | string;
  room_name?: string;
  price_full_board?: string | number;
  price_half_board?: string | number;
  price_breakfast_only?: string | number;
  price_room_only?: string | number;
  max_occupancy?: string | number;
  room_description?: string;
  room_image_url?: string;
}

interface JsonHotel {
  id?: number | string;
  name?: string;
  location?: string;
  address?: string;
  stars?: string | number;
  description?: string;
  amenities?: string;
  image_url?: string;
  cancellation_policy?: string | null;
  rooms?: JsonRoom[];
}

const ImportHotels = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{ 
    success: number; 
    skipped: number; 
    errors: string[];
    roomsImported: number;
  } | null>(null);
  const { userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const canImport = userRole === "ceo" || userRole === "general_manager";

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to import hotels.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const cleanValue = (value: any): string | null => {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (!text || text === "-" || text.toLowerCase() === "null") return null;
    return text;
  };

  const parseNumber = (value: any): number | null => {
    const cleaned = cleanValue(value);
    if (!cleaned) return null;
    const num = Number(cleaned.replace(/,/g, ""));
    return Number.isNaN(num) ? null : num;
  };

  const parseId = (value: any): number | null => {
    const num = parseNumber(value);
    return num === null ? null : Math.trunc(num);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/json") {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid JSON file",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setProgress(0);
    setResults(null);

    const errors: string[] = [];

    try {
      const fileContent = await file.text();
      const parsed = JSON.parse(fileContent);
      const jsonData: JsonHotel[] = Array.isArray(parsed)
        ? parsed
        : (parsed?.items || parsed?.data || parsed?.hotels || []);

      if (!Array.isArray(jsonData)) {
        throw new Error("JSON file must contain an array of hotels");
      }

      let success = 0;
      let skipped = 0;
      let roomsImported = 0;

      for (let i = 0; i < jsonData.length; i++) {
        const hotel = jsonData[i];

        try {
          const rooms = (hotel.rooms || []).map((room) => ({
            id: parseId(room.id) ?? undefined,
            room_name: cleanValue(room.room_name),
            room_description: cleanValue(room.room_description),
            room_image_url: cleanValue(room.room_image_url),
            price_room_only: parseNumber(room.price_room_only),
            price_breakfast: parseNumber(room.price_breakfast_only),
            price_half_board: parseNumber(room.price_half_board),
            price_full_board: parseNumber(room.price_full_board),
            max_occupancy: parseId(room.max_occupancy),
          }));

          const payload = {
            id: parseId(hotel.id) ?? undefined,
            hotel_name: cleanValue(hotel.name) || "",
            location: cleanValue(hotel.location),
            address: cleanValue(hotel.address),
            stars: parseId(hotel.stars),
            description: cleanValue(hotel.description),
            amenities: cleanValue(hotel.amenities),
            image_url: cleanValue(hotel.image_url),
            cancellation_policy: cleanValue(hotel.cancellation_policy),
            rooms,
          };

          await createHotel(payload);
          success++;
          roomsImported += rooms.length;
        } catch (err: any) {
          const message = err?.message || "Unknown error";
          if (message.toLowerCase().includes("already exists") || message.toLowerCase().includes("in use")) {
            skipped++;
          } else {
            errors.push(`Row ${i + 1}: ${message}`);
          }
        }

        setProgress(((i + 1) / jsonData.length) * 100);
      }

      setResults({ success, skipped, errors, roomsImported });
      toast({
        title: "Import completed",
        description: `Successfully imported ${success} hotels with ${roomsImported} rooms. Skipped: ${skipped}, Errors: ${errors.length}`,
      });
    } catch (error: any) {
      console.error("Import error:", error);
      toast({
        title: "Import failed",
        description: "There was an error processing the file",
        variant: "destructive",
      });
      setResults({ success: 0, skipped: 0, errors: [error.message || "Unknown error"], roomsImported: 0 });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Import Hotels</h2>
            <p className="text-muted-foreground">
              Import hotels and their rooms from JSON file
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/hotels")}>Back to Hotels</Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload JSON File</CardTitle>
            <CardDescription>
              Select a JSON file containing hotels with their rooms. The system will automatically
              generate new IDs and handle room relationships.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="file">JSON File</Label>
              <Input
                id="file"
                type="file"
                accept=".json"
                onChange={handleFileChange}
                disabled={importing}
              />
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {importing && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Importing...</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {results && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-semibold">Import Results:</p>
                    <p>Hotels imported: {results.success}</p>
                    <p>Rooms imported: {results.roomsImported}</p>
                    <p>Hotels skipped (already exist): {results.skipped}</p>
                    <p>Errors: {results.errors.length}</p>
                    {results.errors.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
                        {results.errors.slice(0, 20).map((error, idx) => (
                          <p key={idx} className="text-red-600">{error}</p>
                        ))}
                        {results.errors.length > 20 && (
                          <p className="text-muted-foreground italic">
                            ... and {results.errors.length - 20} more errors
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleImport}
              disabled={!file || importing}
              className="w-full"
            >
              <Upload className="mr-2 h-4 w-4" />
              {importing ? "Importing..." : "Import Hotels"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ImportHotels;
